<template>
  <div>
    <div v-for="setting in $props.settings" :key="setting.id" class="checkbox-group">
      <div class="label">{{ setting.name }}</div>
      <div>{{ setting.operation === "OR" ? $t('nolaNovelCheckboxGroups.operationOr') : $t('nolaNovelCheckboxGroups.operationAnd') }}</div>
      <div v-for="option in setting.options" :key="option.id" :class="{ disabled: option.disabled || alreadyChecked(option.name) }">
        <CheckboxItem :checked="$props.item?.tags?.includes(option.name)" @check="(v) => onClick(option.name, v)">
          <span class="checkbox-label" :class="{ readonly: $props.readonly }"> #{{ option.name }} </span>
        </CheckboxItem>
      </div>
    </div>
    <div v-if="$props.error" class="error-message">{{ $props.error }}</div>
  </div>
</template>

<script lang="ts">
import CheckboxItem from "@/components/atoms/CheckboxItem.vue";
import Vue from "vue";

interface Props {
  item: {
    tags: string[];
  };
  settings: {
    id: string;
    name: string;
    operation: string;
    options: {
      id: string;
      name: string;
      defaultValue: boolean;
      disabled: boolean;
    }[];
  }[];
  error: string | boolean;
  nolaNovelItem: {
    tags: string[];
  };
  readonly: boolean;
}

interface Data {}

interface Computed {
  tag: string[];
}

interface Methods {
  onClick: (tag: string, checked: boolean) => void;
  alreadyChecked: (tag: string) => boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CheckboxItem,
  },
  model: {
    prop: "item",
    event: "select",
  },
  props: {
    item: {
      type: Object,
    },
    settings: {
      type: Array,
      required: true,
    },
    nolaNovelItem: {
      type: Object,
    },
    error: {},
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    if (this.$props.item?.tags) {
      return;
    }

    const tags = [] as string[];
    this.$props.settings.forEach((setting) => {
      setting.options.forEach((option) => {
        if (option.defaultValue || this.alreadyChecked(option.name)) {
          tags.push(option.name);
        }
      });
    });
    this.$emit("select", {
      tags,
    });
  },
  methods: {
    onClick(tag: string, checked: boolean) {
      if (this.$props.readonly) {
        return;
      }

      const tags = this.$props.item?.tags ?? { tags: [] as string[] };
      if (checked) {
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
      } else {
        const index = tags.indexOf(tag);
        if (index !== -1) {
          tags.splice(index, 1);
        }
      }

      this.$emit("select", {
        tags,
      });
    },
    alreadyChecked(tag: string) {
      return this.$props.nolaNovelItem?.tag?.includes(tag);
    },
  },
});
</script>
<style lang="scss" scoped>
.checkbox-group {
  padding-block: 5px;

  .label {
    padding-top: 5px;
    font-weight: var(--font-weight-bold);
    font-size: 1.2em;
  }

  .disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .checkbox-label {
    padding-block: 5px;
    font-weight: var(--font-weight-bold);
  }

  .checkbox-label:not(.readonly) {
    cursor: pointer;
  }
}

.error-message {
  color: var(--color-warning);
  padding-block: 5px;
}
</style>
